import type { CollectionRequirementSettings } from "@repo/shared";
import type {
  ConfiguredAssessmentType,
  FlowFieldOption,
  FlowQuizSummary,
  PdfLayoutColumn,
  PdfLayoutType,
} from "@repo/shared/types";
import { ArrowLeft, ChevronDown, GripVertical, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import { getPdfComponentLabel } from "@/components/pdf-templates/pdf-component-labels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/hooks/use-confirm";
import { usePdfTemplateAdvisorNotePages } from "@/lib/hooks/use-advisor-note-pages";
import {
  useCreateLayout,
  useDeleteComponent,
  useDeleteLayout,
  useDuplicateComponent,
  useMoveComponentToStandalone,
  useReorderColumnComponents,
  useReorderItems,
  useUpdateComponentOrder,
} from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { ComponentCard, hasConditionalDisplay } from "./component-card";
import { ComponentDialog } from "./component-dialog";
import { LayoutColumn } from "./layout-column";
import { LayoutSettingsDialog } from "./layout-settings-dialog";
import { T } from "./pdf-editor.i18n";
import type { PdfComponent, PdfComponentType, PdfLayout, PdfTemplatePage } from "./types";

type FlowField = {
  id: string;
  name: string;
  type: string;
  // Option rows with stable ids (QL-201); rule values reference the id.
  options: FlowFieldOption[];
};

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  isStandard?: boolean;
};

type PdfEditorProps = {
  templateId: string;
  flowId: string;
  initialPage: PdfTemplatePage;
  flowFields: FlowField[];
  /** QL-13 — the `quiz` components on this template's flow. */
  quizzes: FlowQuizSummary[];
  /** QL-13 — while true the quiz list is a placeholder (loading or failed), which must not read as "all dangling". */
  quizzesUnknown?: boolean;
  /** QL-361 — the configured assessment types of this template's flow. */
  configuredAssessmentTypes: ConfiguredAssessmentType[];
  /** QL-361 — while true the list is a placeholder (loading or failed), which must not read as "all stale". */
  assessmentTypesUnknown?: boolean;
  clientCustomFields?: ClientCustomField[];
  hasXRayAccess: boolean;
  hasRiskModel?: boolean;
  /** QL-212 — the flow's risk model, so the component picker can resolve its input scopes. */
  riskModelId?: string | null;
  portfolioCollectionId?: string | null;
  templateLanguage: string;
  collectionSettings?: CollectionRequirementSettings | null;
  sectionId?: string;
  sectionScope?: "global" | "per_goal" | "per_portfolio";
  sectionName?: string;
  onBack?: () => void;
  onSectionSettings?: () => void;
};

export function PdfEditor({
  templateId,
  flowId: _flowId,
  initialPage,
  flowFields,
  quizzes,
  quizzesUnknown = false,
  configuredAssessmentTypes,
  assessmentTypesUnknown = false,
  clientCustomFields = [],
  hasXRayAccess,
  hasRiskModel = false,
  riskModelId = null,
  portfolioCollectionId = null,
  templateLanguage,
  collectionSettings,
  sectionId: _sectionId,
  sectionScope,
  sectionName,
  onBack,
  onSectionSettings,
}: PdfEditorProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  // QL-414 — the flow pages that take advisor notes, for the Insert Field menus.
  const { pages: advisorNotePages, isUnknown: advisorNotePagesUnknown } =
    usePdfTemplateAdvisorNotePages(templateId);
  const [loading, setLoading] = useState(false);

  // PDF Component type labels — resolved via the shared canonical registry
  // (pdf-component-labels.ts) so this editor can never drift from the
  // add-component dialog or the admin usage page.
  const getComponentLabel = (componentType: PdfComponentType): string =>
    getPdfComponentLabel(componentType, userLanguage);

  const deleteComponentMutation = useDeleteComponent();
  const duplicateComponentMutation = useDuplicateComponent();
  const createLayoutMutation = useCreateLayout();
  const deleteLayoutMutation = useDeleteLayout();
  const updateComponentOrderMutation = useUpdateComponentOrder();
  const reorderItemsMutation = useReorderItems();
  const moveToStandaloneMutation = useMoveComponentToStandalone();
  const reorderColumnMutation = useReorderColumnComponents();

  const [selectedComponent, setSelectedComponent] = useState<PdfComponent | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [addComponentDialogOpen, setAddComponentDialogOpen] = useState(false);
  const [targetLayoutColumn, setTargetLayoutColumn] = useState<{
    layoutId: string;
    column: PdfLayoutColumn;
  } | null>(null);
  const [layoutSettingsDialogOpen, setLayoutSettingsDialogOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<PdfLayout | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const [draggedItem, setDraggedItem] = useState<{
    id: string;
    type: "component" | "layout" | "page-item" | "layout-component";
    itemType?: "component" | "layout";
    componentType?: PdfComponentType;
    sourceLayoutId?: string;
    sourceColumn?: PdfLayoutColumn;
  } | null>(null);
  const [localDragState, setLocalDragState] = useState<{
    componentId: string;
    layoutId: string;
    column: PdfLayoutColumn;
    index: number;
  } | null>(null);

  // Helper function to check if an item can be dropped into a layout
  const canDropIntoLayout = (item: typeof draggedItem): boolean => {
    if (!item) return true;

    const isLayoutBlock = item.type === "page-item" && item.itemType === "layout";
    const isPageBreak = item.componentType === "page_break";

    return !isLayoutBlock && !isPageBreak;
  };

  // Combined page items (layouts and standalone components)
  const pageItems = [
    ...(initialPage.layouts || []).map((l) => ({
      type: "layout" as const,
      id: l.id,
      order: l.order,
      data: l,
    })),
    ...initialPage.components.map((c) => ({
      type: "component" as const,
      id: c.id,
      order: c.order,
      data: c,
    })),
  ].sort((a, b) => a.order - b.order);

  const handleDeleteComponent = async (componentId: string) => {
    const confirmed = await confirm({
      title: t.deleteComponent,
      description: t.deleteComponentConfirmation,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await deleteComponentMutation.mutateAsync(componentId);
      if (selectedComponent?.id === componentId) {
        setSelectedComponent(null);
      }
    } catch (err) {
      await confirm({
        title: t.error,
        description: err instanceof Error ? err.message : t.failedToDelete,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }

    setLoading(false);
  };

  const handleDuplicateComponent = async (componentId: string) => {
    setLoading(true);
    try {
      await duplicateComponentMutation.mutateAsync(componentId);
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToDuplicate);
    }
    setLoading(false);
  };

  const handleAddLayout = async (layoutType: PdfLayoutType) => {
    setLoading(true);
    try {
      await createLayoutMutation.mutateAsync({ pageId: initialPage.id, layoutType });
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToCreate);
    }

    setLoading(false);
  };

  const handleDeleteLayout = async (layout: PdfLayout) => {
    const isSamePage = layout.layoutType === "same_page";
    const confirmed = await confirm({
      title: isSamePage ? t.deleteSamePageBlock : t.delete2ColumnBlock,
      description: isSamePage
        ? t.deleteSamePageBlockConfirmation
        : t.delete2ColumnBlockConfirmation,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await deleteLayoutMutation.mutateAsync(layout.id);
    } catch (err) {
      await confirm({
        title: t.error,
        description: err instanceof Error ? err.message : t.failedToDelete,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }

    setLoading(false);
  };

  const handleComponentSettings = (component: PdfComponent) => {
    setSelectedComponent(component);
    setConfigDialogOpen(true);
  };

  const handleLayoutSettings = (layout: PdfLayout) => {
    setSelectedLayout(layout);
    setLayoutSettingsDialogOpen(true);
  };

  // Drag handlers
  const handleDragStart = (
    e: React.DragEvent,
    id: string,
    type: "component" | "layout",
    sourceLayoutId?: string,
    sourceColumn?: PdfLayoutColumn,
    sourceIndex?: number,
    componentType?: PdfComponentType,
  ) => {
    // If dragging from within a layout column, create a column-width drag preview
    if (sourceLayoutId && sourceColumn) {
      // Get the actual width of the column container
      const columnElement = (e.target as HTMLElement).closest(".space-y-2") as HTMLElement;
      const elementWidth = columnElement ? columnElement.offsetWidth : 400;

      // Create custom drag preview - half-width box matching actual column width
      const dragPreview = document.createElement("div");
      dragPreview.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: ${elementWidth}px;
        height: 60px;
        background: rgba(59, 130, 246, 0.2);
        border: 2px dashed rgb(59, 130, 246);
        border-radius: 8px;
        padding: 16px;
        font-size: 14px;
        font-weight: 500;
        color: rgb(59, 130, 246);
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      dragPreview.textContent = t.movingComponent;
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 20, 30);

      // Clean up after next frame
      requestAnimationFrame(() => {
        document.body.removeChild(dragPreview);
      });

      // Track local drag state for same-column reordering
      if (sourceIndex !== undefined) {
        setLocalDragState({
          componentId: id,
          layoutId: sourceLayoutId,
          column: sourceColumn,
          index: sourceIndex,
        });
      }
    }
    // Also set localDragState for standalone components being dragged
    else if (!sourceLayoutId && !sourceColumn && sourceIndex === undefined) {
      // Standalone component being dragged - no local state needed
      setLocalDragState(null);
    }

    // Use "layout-component" type when dragging from within a layout
    const dragType = sourceLayoutId && sourceColumn ? "layout-component" : type;
    setDraggedItem({ id, type: dragType, componentType, sourceLayoutId, sourceColumn });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setLocalDragState(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropOnLayout = async (
    layoutId: string,
    column: PdfLayoutColumn,
    e: React.DragEvent,
    targetIndex?: number,
  ) => {
    e.preventDefault();
    if (!draggedItem) return;

    // Handle component, page-item, and layout-component drops
    if (
      draggedItem.type !== "component" &&
      draggedItem.type !== "page-item" &&
      draggedItem.type !== "layout-component"
    )
      return;

    setLoading(true);

    // Check if this is same-column reordering
    if (
      localDragState &&
      draggedItem.type === "layout-component" &&
      draggedItem.sourceLayoutId === layoutId &&
      localDragState.column === column &&
      targetIndex !== undefined
    ) {
      // Same column reordering - use reorderComponentsInColumn
      const sourceIndex = localDragState.index;

      // Don't reorder if dropping on itself
      if (sourceIndex === targetIndex) {
        setLoading(false);
        setDraggedItem(null);
        setLocalDragState(null);
        return;
      }

      const layout = initialPage.layouts?.find((l) => l.id === layoutId);
      const sourceComponents = layout?.components.filter((c) => c.columnPosition === column) || [];
      const componentsArray = [...sourceComponents];
      const [movedComponent] = componentsArray.splice(sourceIndex, 1);

      // Adjust target index if moving forward (since we removed an item before the target)
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      componentsArray.splice(adjustedTargetIndex, 0, movedComponent);

      const componentOrders = componentsArray.map((component, index) => ({
        id: component.id,
        order: index,
      }));

      await reorderColumnMutation.mutateAsync({
        layoutId,
        column,
        data: { componentOrders },
      });

      setDraggedItem(null);
      setLocalDragState(null);
      setLoading(false);
      return;
    }

    // Cross-column or new component move
    // Get the order based on existing components in target column
    const layout = initialPage.layouts?.find((l) => l.id === layoutId);
    const componentsInColumn = layout?.components.filter((c) => c.columnPosition === column) || [];
    const newOrder = targetIndex !== undefined ? targetIndex : componentsInColumn.length;

    // Update component to new layout and column
    try {
      await updateComponentOrderMutation.mutateAsync({
        componentId: draggedItem.id,
        data: { order: newOrder, layoutId, columnPosition: column },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToReorder);
    }

    setDraggedItem(null);
    setLocalDragState(null);
    setLoading(false);
  };

  const handleDropStandalone = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem) return;

    // Only handle component drops
    if (draggedItem.type !== "component") return;

    // Don't do anything if already standalone
    if (!draggedItem.sourceLayoutId) {
      setDraggedItem(null);
      return;
    }

    setLoading(true);

    // Get the order based on existing standalone components
    const standaloneComponents = initialPage.components.filter((c) => !c.pdfPageLayoutId);
    const newOrder = standaloneComponents.length;

    // Update component to standalone (no layout)
    try {
      await updateComponentOrderMutation.mutateAsync({
        componentId: draggedItem.id,
        data: { order: newOrder, layoutId: null, columnPosition: null },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToReorder);
    }

    setDraggedItem(null);
    setLoading(false);
  };

  const handleMoveToStandalone = async (componentId: string, targetIndex: number) => {
    setLoading(true);

    try {
      await moveToStandaloneMutation.mutateAsync({
        componentId,
        data: { targetIndex },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToReorder);
    }

    setDraggedItem(null);
    setLocalDragState(null);
    setLoading(false);
  };

  // Handler for reordering page-level items (layouts and standalone components)
  const handleReorderPageItems = async (draggedId: string, targetIndex: number) => {
    setLoading(true);

    // Build current page items list
    const layouts = initialPage.layouts || [];
    const standaloneComponents = initialPage.components.filter((c) => !c.pdfPageLayoutId);
    const allItems: Array<{ id: string; type: "layout" | "component"; order: number }> = [
      ...layouts.map((l) => ({ id: l.id, type: "layout" as const, order: l.order })),
      ...standaloneComponents.map((c) => ({
        id: c.id,
        type: "component" as const,
        order: c.order,
      })),
    ].sort((a, b) => a.order - b.order);

    // Find current position
    const currentIndex = allItems.findIndex((item) => item.id === draggedId);
    if (currentIndex === -1 || currentIndex === targetIndex) {
      setLoading(false);
      setDraggedItem(null);
      return;
    }

    // Reorder
    const reorderedItems = [...allItems];
    const [movedItem] = reorderedItems.splice(currentIndex, 1);
    reorderedItems.splice(targetIndex, 0, movedItem);

    // Build update payload
    const items = reorderedItems.map((item, index) => ({
      id: item.id,
      type: item.type,
      order: index,
    }));

    try {
      await reorderItemsMutation.mutateAsync({
        pageId: initialPage.id,
        data: { pageItems: items },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : t.failedToReorder);
    }

    setDraggedItem(null);
    setLoading(false);
  };

  // One column of a block (QL-384: a Same Page Block has one, `main`).
  const columnProps = (layout: PdfLayout, column: PdfLayoutColumn, title: string) => ({
    column,
    title,
    components: layout.components.filter((c) => c.columnPosition === column),
    loading,
    isDragging: draggedItem !== null,
    canDrop: canDropIntoLayout(draggedItem),
    draggedId: draggedItem?.id ?? null,
    labels: {
      addComponent: t.addComponent,
      dropComponentHere: t.dropComponentHere,
      noComponents: t.noComponents,
      duplicateComponent: t.duplicateComponent,
      settings: t.settings,
      delete: t.delete,
      conditional: t.conditional,
    },
    getLabel: getComponentLabel,
    onAddComponent: () => {
      setTargetLayoutColumn({ layoutId: layout.id, column });
      setAddComponentDialogOpen(true);
    },
    onDrop: (e: React.DragEvent, index?: number) => handleDropOnLayout(layout.id, column, e, index),
    onComponentDragStart: (e: React.DragEvent, component: PdfComponent, index: number) =>
      handleDragStart(
        e,
        component.id,
        "component",
        layout.id,
        column,
        index,
        component.componentType,
      ),
    onComponentDragEnd: handleDragEnd,
    onComponentSettings: handleComponentSettings,
    onComponentDelete: (component: PdfComponent) => handleDeleteComponent(component.id),
    onComponentDuplicate: (component: PdfComponent) => handleDuplicateComponent(component.id),
  });
  return (
    <div className="space-y-4">
      {/* Main Editor Area */}
      <div className="space-y-4">
        {/* Back arrow + section header + actions on one line */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="icon" onClick={onBack} tooltip={t.back}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {sectionName && <h2 className="text-2xl font-bold">{sectionName}</h2>}
            {sectionScope && (
              <Badge variant={sectionScope === "global" ? "secondary" : "outline"}>
                {sectionScope === "global"
                  ? t.globalScope
                  : sectionScope === "per_portfolio"
                    ? t.perPortfolioScope
                    : t.perGoalScope}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            {onSectionSettings && (
              <Button variant="outline" size="sm" onClick={onSectionSettings}>
                {t.sectionSettings}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={loading}>
                  {t.add}
                  <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setAddComponentDialogOpen(true)}>
                  {t.component}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleAddLayout("two_column")}>
                  {t.twoColumnBlockLabel}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleAddLayout("same_page")}>
                  {t.samePageBlockLabel}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Page Items */}
        {pageItems.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground">
            <p>{t.noComponentsYet}</p>
            <p className="text-sm mt-2">{t.addComponentsDescription}</p>
          </Card>
        ) : (
          // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag events
          <div className="space-y-4" onDragOver={handleDragOver} onDrop={handleDropStandalone}>
            {pageItems.map((item, index) => {
              const isDragging = draggedItem?.id === item.id && draggedItem.type === "page-item";

              if (item.type === "layout") {
                const layout = item.data as PdfLayout;
                const isSamePage = layout.layoutType === "same_page";

                return (
                  <Card
                    key={layout.id}
                    className={`p-4 ${isDragging ? "opacity-50" : ""} transition-all`}
                  >
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle requires drag events */}
                    <div
                      className="flex items-center justify-between mb-4 cursor-move"
                      draggable={!loading}
                      onDragStart={(e) => {
                        // Get the actual width of the layout block
                        const layoutElement = (e.currentTarget as HTMLElement).closest(
                          ".p-4",
                        ) as HTMLElement;
                        const elementWidth = layoutElement ? layoutElement.offsetWidth : 800;

                        // Custom drag preview: the block's shape — two columns, or
                        // one full-width column for a Same Page Block (QL-384).
                        const dragPreview = document.createElement("div");
                        dragPreview.style.cssText = `
                          position: fixed;
                          top: -9999px;
                          left: -9999px;
                          width: ${elementWidth}px;
                          height: 120px;
                          background: rgba(59, 130, 246, 0.2);
                          border: 2px dashed rgb(59, 130, 246);
                          border-radius: 8px;
                          padding: 16px;
                        `;
                        const previewColumn = `<div style="background: rgba(59, 130, 246, 0.3); border-radius: 4px; border: 1px dashed rgb(59, 130, 246);"></div>`;
                        dragPreview.innerHTML = `
                          <div style="display: grid; grid-template-columns: ${isSamePage ? "1fr" : "1fr 1fr"}; gap: 8px; height: 100%;">
                            ${isSamePage ? previewColumn : previewColumn + previewColumn}
                          </div>
                        `;
                        document.body.appendChild(dragPreview);
                        e.dataTransfer.setDragImage(dragPreview, 20, 60);

                        // Clean up after next frame
                        requestAnimationFrame(() => {
                          document.body.removeChild(dragPreview);
                        });

                        e.dataTransfer.effectAllowed = "move";
                        setDraggedItem({
                          id: layout.id,
                          type: "page-item",
                          itemType: "layout",
                          componentType: undefined,
                        });
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        e.currentTarget.classList.add("ring-2", "ring-primary");
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove("ring-2", "ring-primary");
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("ring-2", "ring-primary");

                        if (draggedItem?.type === "layout-component") {
                          // Component from layout being dropped - move to standalone before this layout
                          handleMoveToStandalone(draggedItem.id, index);
                        } else if (
                          draggedItem?.type === "page-item" &&
                          draggedItem.id !== layout.id
                        ) {
                          // Reorder page items - insert at this position (before)
                          handleReorderPageItems(draggedItem.id, index);
                        }
                      }}
                      onDragEnd={() => setDraggedItem(null)}
                    >
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">
                          {isSamePage ? t.samePageBlockLabel : t.twoColumnLayoutBlock}
                        </span>
                        {isSamePage && (
                          <span className="text-xs text-muted-foreground">
                            {t.samePageBlockHint}
                          </span>
                        )}
                        {hasConditionalDisplay(layout.config) && (
                          <Badge variant="secondary" className="text-xs">
                            {t.conditional}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          tooltip={t.settings}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLayoutSettings(layout);
                          }}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          tooltip={t.delete}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLayout(layout);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {isSamePage ? (
                      <LayoutColumn {...columnProps(layout, "main", t.components)} />
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <LayoutColumn {...columnProps(layout, "left", t.leftColumn)} />
                        <LayoutColumn {...columnProps(layout, "right", t.rightColumn)} />
                      </div>
                    )}
                  </Card>
                );
              } else {
                // Standalone component with drag & drop reordering
                const component = item.data as PdfComponent;
                return (
                  // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop requires interactive div
                  <div
                    key={component.id}
                    draggable={!loading}
                    onDragStart={(e) => {
                      // Get the actual width of the element being dragged
                      const elementWidth = (e.currentTarget as HTMLElement).offsetWidth;

                      // Create custom drag preview - matching flow editor pattern
                      const dragPreview = document.createElement("div");
                      dragPreview.style.cssText = `
                        position: fixed;
                        top: -9999px;
                        left: -9999px;
                        width: ${elementWidth}px;
                        height: 60px;
                        background: rgba(59, 130, 246, 0.2);
                        border: 2px dashed rgb(59, 130, 246);
                        border-radius: 8px;
                        padding: 16px;
                        font-size: 14px;
                        font-weight: 500;
                        color: rgb(59, 130, 246);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      `;
                      dragPreview.textContent = t.movingComponent;
                      document.body.appendChild(dragPreview);
                      e.dataTransfer.setDragImage(dragPreview, 20, 30);

                      // Clean up after next frame
                      requestAnimationFrame(() => {
                        document.body.removeChild(dragPreview);
                      });

                      e.dataTransfer.effectAllowed = "move";
                      setDraggedItem({
                        id: component.id,
                        type: "page-item",
                        itemType: "component",
                        componentType: component.componentType,
                      });
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.classList.add("ring-2", "ring-primary");
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("ring-2", "ring-primary");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("ring-2", "ring-primary");

                      if (draggedItem?.type === "layout-component") {
                        // Component from layout being dropped - move to standalone position
                        handleMoveToStandalone(draggedItem.id, index);
                      } else if (
                        draggedItem?.type === "page-item" &&
                        draggedItem.id !== component.id
                      ) {
                        // Reorder page items - insert at this position (before)
                        handleReorderPageItems(draggedItem.id, index);
                      }
                    }}
                    onDragEnd={() => setDraggedItem(null)}
                    className={`${isDragging ? "opacity-50" : ""} transition-all`}
                  >
                    <ComponentCard
                      component={component}
                      onSettings={() => handleComponentSettings(component)}
                      onDelete={() => handleDeleteComponent(component.id)}
                      onDuplicate={() => handleDuplicateComponent(component.id)}
                      duplicateLabel={t.duplicateComponent}
                      settingsLabel={t.settings}
                      deleteLabel={t.delete}
                      conditionalLabel={t.conditional}
                      onDragStart={(e) =>
                        handleDragStart(
                          e,
                          component.id,
                          "component",
                          undefined,
                          undefined,
                          undefined,
                          component.componentType,
                        )
                      }
                      onDragEnd={handleDragEnd}
                      getLabel={getComponentLabel}
                    />
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>

      {/* Unified Component Dialog (for both add and edit) */}
      <ComponentDialog
        open={configDialogOpen || addComponentDialogOpen}
        flowFields={flowFields}
        quizzes={quizzes}
        advisorNotePages={advisorNotePages}
        advisorNotePagesUnknown={advisorNotePagesUnknown}
        quizzesUnknown={quizzesUnknown}
        configuredAssessmentTypes={configuredAssessmentTypes}
        assessmentTypesUnknown={assessmentTypesUnknown}
        clientCustomFields={clientCustomFields}
        hasXRayAccess={hasXRayAccess}
        hasRiskModel={hasRiskModel}
        riskModelId={riskModelId}
        portfolioCollectionId={portfolioCollectionId}
        templateLanguage={templateLanguage}
        collectionSettings={collectionSettings}
        sectionScope={sectionScope}
        isFirstHeading={!initialPage.components.some((c) => c.componentType === "heading")}
        // Add mode props
        pageId={addComponentDialogOpen ? initialPage.id : undefined}
        layoutId={targetLayoutColumn?.layoutId || null}
        columnPosition={targetLayoutColumn?.column || null}
        // Edit mode props
        component={configDialogOpen ? selectedComponent : null}
        onClose={(success) => {
          setConfigDialogOpen(false);
          setAddComponentDialogOpen(false);
          setTargetLayoutColumn(null);
          if (success) {
            setSelectedComponent(null);
          }
        }}
      />

      {/* Layout Settings Dialog */}
      <LayoutSettingsDialog
        open={layoutSettingsDialogOpen}
        layout={selectedLayout}
        availableFields={flowFields}
        sectionScope={sectionScope}
        portfolioCollectionId={portfolioCollectionId}
        configuredAssessmentTypes={configuredAssessmentTypes}
        assessmentTypesUnknown={assessmentTypesUnknown}
        quizzes={quizzes}
        quizzesUnknown={quizzesUnknown}
        templateLanguage={templateLanguage}
        onClose={(success) => {
          setLayoutSettingsDialogOpen(false);
          if (success) {
            setSelectedLayout(null);
          }
        }}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </div>
  );
}
